import type { CollectionSummaryJSONObject, TagJSONObject } from '@ikegps/core-api';
import type { ParseJSONObject } from '@ikegps/core-api';
import { action, computed, observable, makeObservable } from 'mobx';
import { sort as timSort } from 'timsort';
import type { MaterialIcon } from '../icon/icons';
import type { T } from '../../i18n';

export type SortablePrimitive = Date | number | string | null;

export interface SortableStateOption {
	title: T;
	icon: MaterialIcon;
	visible?: () => boolean;
	keys: {
		tag: string | ((tag: ParseJSONObject) => SortablePrimitive);
		collection: string | ((collectionSummary: ParseJSONObject) => SortablePrimitive);
	};
	value?: {
		collection?: (collectionSummary: ParseJSONObject) => SortablePrimitive;
	};
	info: {
		tag?: boolean;
		collection?: boolean;
	};
}

export enum SortDirection {
	ASCENDING = 1,
	DESCENDING = -1,
}

export class SortableState {
	@observable direction: SortDirection = SortDirection.DESCENDING;
	@observable option: SortableStateOption | null = null;

	options: SortableStateOption[];

	constructor(sortOptions: SortableStateOption[]) {
		makeObservable(this);

		this.options = sortOptions;
		if (this.options.length > 0) {
			this.option = this.options[0];
		}
	}

	@computed get isAscending() {
		return this.direction === SortDirection.ASCENDING;
	}

	@action.bound toggleDirection() {
		if (this.direction === SortDirection.ASCENDING) {
			this.direction = SortDirection.DESCENDING;
		} else {
			this.direction = SortDirection.ASCENDING;
		}
	}

	@action setSort(sortOption: SortableStateOption) {
		this.option = sortOption;
	}

	sortTags(objs: TagJSONObject[]): TagJSONObject[] {
		return this.sortObjects(objs, 'tag');
	}

	sortSummaries(objs: CollectionSummaryJSONObject[]): CollectionSummaryJSONObject[] {
		return this.sortObjects(objs, 'collection');
	}

	getCollectionDisplayValue(obj: CollectionSummaryJSONObject): SortablePrimitive {
		const currentOption = this.option;
		if (currentOption.info.collection == null || currentOption.info.collection === false) {
			return null;
		}

		if (currentOption.value != null && currentOption.value.collection != null) {
			return currentOption.value.collection(obj);
		}

		const sortKey = currentOption.keys.collection;
		if (typeof sortKey === 'string') {
			return obj[sortKey];
		}

		return sortKey(obj);
	}

	sortObjects<Z extends ParseJSONObject>(objs: Z[], key: 'collection' | 'tag'): Z[] {
		const currentOption = this.option;
		const direction = this.direction;
		const sortKey = currentOption.keys[key];
		const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

		function compareFunc(a: Z, b: Z) {
			let aKey;
			let bKey;
			if (typeof sortKey === 'string') {
				if (sortKey === 'title') {
					aKey = a['title'] || a['subtitle'];
					bKey = b['title'] || b['subtitle'];
				} else {
					aKey = a[sortKey];
					bKey = b[sortKey];
				}
			} else {
				aKey = sortKey(a);
				bKey = sortKey(b);
			}

			// Ensure a stable sort by using updatedAt and objectId as a extra indexes
			if (aKey === bKey) {
				if (a.updatedAt !== b.updatedAt) {
					aKey = a.updatedAt;
					bKey = b.updatedAt;
				} else {
					aKey = a.objectId;
					bKey = b.objectId;
				}
			}

			// only use collator for title otherwise fallback to default sorting
			if (sortKey === 'title') {
				return collator.compare(aKey, bKey) * direction;
			}

			// IKE4-2322 Explicitly compare against null and undefined, where equality is problematic.
			if (aKey == null || bKey == null) {
				if (aKey != null && bKey == null) {
					return direction;
				} else if (aKey == null && bKey != null) {
					return -1 * direction;
				}
				return 0;
			} else if (aKey > bKey) {
				return direction;
			} else if (aKey < bKey) {
				return -1 * direction;
			}

			return 0;
		}

		timSort(objs, compareFunc);

		return objs;
	}
}
